
import type { ShopSlot_, ShopStock } from "../net/connection";
import { money } from "../money";
import { itemArtNode } from "./itemart";
import { CHROME_ASSETS, elem, installChrome, styleInstaller } from "./chrome";
import { iconCellsTall } from "./itemicon";
import { ItemTooltip, attachHoverTooltip, slotTooltipData, type ItemTooltipData } from "./tooltip";

export interface ShopActions {
  /** Buy the shelf in this cell of the shop grid. */
  onBuy?: (slot: number) => void;
  /** The player shut the window. Sent on so the server can forget it too. */
  onClose?: () => void;
  /**
   * Buy/Sell toggle changed. Sell selected is a hint that the bag should enter
   * sell mode; the shop grid itself still buys on click.
   */
  onSellMode?: (selling: boolean) => void;
}

/** `shopdialog.py` window width. */
export const SHOP_WIDTH = 184;
/** `shopdialog.py` window height. */
export const SHOP_HEIGHT = 328;
/** `ItemSlot.x_count` (`shopdialog.py`). */
export const SHOP_COLUMNS = 5;
/** `ItemSlot.y_count` (`shopdialog.py`). */
export const SHOP_ROWS = 8;
/** `ItemSlot` cell count: 5 × 8. */
export const SHOP_SLOT_COUNT = SHOP_COLUMNS * SHOP_ROWS;
/** `ItemSlot.x_step` / `y_step`. */
export const SHOP_SLOT_SIZE = 32;
/** How far in from the right edge `ShopDialog` opens. */
export const SHOP_X_FROM_RIGHT = 400; // shopdialog.py:6
/** `ShopDialog`'s y. */
export const SHOP_Y = 10; // shopdialog.py:7
/** `SHOP_TITLE` (`locale_interface.txt`). */
export const SHOP_TITLE = "Shop";

const STYLE_ID = "shop-style";

const STYLE = `
.shop {
  position: fixed;
  /* shopdialog.py: x = SCREEN_WIDTH - 400, y = 10 -- left of the inventory. */
  left: max(8px, calc(100vw - ${SHOP_X_FROM_RIGHT}px));
  top: max(${SHOP_Y}px, calc(env(safe-area-inset-top) + ${SHOP_Y}px));
  z-index: 40;
  width: ${SHOP_WIDTH}px;
  height: ${SHOP_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  pointer-events: auto;
  overflow: hidden;
}
.shop .m2-titlebar {
  width: 169px;
  left: 8px;
  top: 8px;
  cursor: move;
  user-select: none;
}
.shop-grid {
  position: absolute;
  left: 12px;
  top: 34px;
  display: grid;
  grid-template-columns: repeat(${SHOP_COLUMNS}, ${SHOP_SLOT_SIZE}px);
  grid-template-rows: repeat(${SHOP_ROWS}, ${SHOP_SLOT_SIZE}px);
  width: ${SHOP_COLUMNS * SHOP_SLOT_SIZE}px;
  height: ${SHOP_ROWS * SHOP_SLOT_SIZE}px;
}
.shop-slot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  /* Not a cursor of its own: the counter's cursor is the window's, and the
     window's is Buy or Sell for as long as one of the two toggles is down
     (uishop.py:258-271). A "pointer" here would cover the very cursor the
     shop exists to show. ui/cursor.ts writes it on body; these inherit. */
  cursor: inherit;
  color: inherit;
  overflow: visible;
}
.shop-slot.shop-row:hover { filter: brightness(1.15); }
.shop-slot.shop-row-unaffordable { opacity: 0.45; }
.shop-glyph {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.shop-slot .item-icon {
  position: absolute;
  top: 0;
  left: 0;
  width: ${SHOP_SLOT_SIZE}px;
  height: calc(${SHOP_SLOT_SIZE}px * var(--shop-item-span, 1));
  margin: 0;
  flex: 0 0 auto;
  pointer-events: none;
  z-index: 1;
}
.shop-slot-1 .bag-glyph { color: #cfd8e3; }
.shop-slot-2 .bag-glyph { color: #d3a877; }
.shop-slot-3 .bag-glyph { color: #e2564f; }
.shop-slot-4 .bag-glyph { color: #d8c58a; }
.shop-count {
  position: absolute;
  right: 1px;
  bottom: 0;
  font: 10px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #fff6d0;
  text-shadow: 0 0 2px #000, 1px 1px 0 #000;
  pointer-events: none;
}
.shop-price {
  position: absolute;
  left: 1px;
  top: 0;
  font: 9px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #ffd27a;
  text-shadow: 0 0 2px #000, 1px 1px 0 #000;
  pointer-events: none;
  max-width: 30px;
  overflow: hidden;
}
.shop-row-unaffordable .shop-price { color: #e2564f; }
.shop-buy, .shop-sell { position: absolute; top: 295px; }
.shop-buy { left: 21px; }
.shop-sell { left: 104px; }
/* The pressed tab is the down face the layout gives the button's third state
   (shopdialog.py:79, :97), held rather than shown only while the pointer is
   on it. */
.shop-buy.shop-mode-on, .shop-sell.shop-mode-on {
  background-image: url("${CHROME_ASSETS.middleDown}");
  color: #ffe6ba;
}
.shop-empty {
  position: absolute;
  left: 12px;
  right: 12px;
  top: 140px;
  color: #9b9484;
  font: 11px/1.35 Tahoma, "MS Sans Serif", sans-serif;
  text-align: center;
  pointer-events: none;
}
.shop-hint {
  position: absolute;
  left: 8px;
  right: 8px;
  top: 272px;
  color: #9b9484;
  font: 10px/1.2 Tahoma, "MS Sans Serif", sans-serif;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

/** The shop grid's rules, for the stall that draws the same `ShopDialog`. */
export const installShopStyle = installStyle;

export function shelfLabel(name: string, count: number): string {
  return count > 1 ? `${name} x${count}` : name;
}

/** Whether a purse covers a shelf. A hint for the drawing, never a gate on the
 *  click: the server decides, and it decides against a character rather than
 *  against the last `Bag` frame this client happened to receive. */
export function affordable(price: number, gold: number): boolean {
  return gold >= price;
}

/** The fallback silhouette, at the size a shelf has room for. */
function shelfTooltipData(shelf: ShopSlot_): ItemTooltipData {
  return slotTooltipData(shelf, { price: shelf.price });
}

export class ShopPanel {
  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly cells: HTMLButtonElement[] = [];
  private readonly buyBtn: HTMLButtonElement;
  private readonly sellBtn: HTMLButtonElement;
  private readonly emptyEl: HTMLElement;
  private readonly hintEl: HTMLElement;
  /** The counter as last sent. Kept so a `Bag` frame -- which changes what is
   *  affordable without changing what is for sale -- can repaint against it. */
  private stock: ShopStock = { npcId: 0, name: "", items: [] };
  private gold = 0;
  private fingerprint = "";
  /** Buy selected (default). Sell is a visual / `onSellMode` hint only. */
  private sellMode = false;
  /** Live shelf under each grid cell; tooltips read this rather than closing over paint. */
  private readonly shelfAt: (ShopSlot_ | undefined)[] = Array.from({ length: SHOP_SLOT_COUNT });
  private readonly tooltip: ItemTooltip;
  private drag: { readonly ox: number; readonly oy: number; readonly sl: number; readonly st: number } | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: ShopActions = {},
  ) {
    const doc = container.ownerDocument;
    installChrome(doc);
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.root = elem(doc, "div", "shop m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "false");
    this.root.style.width = `${SHOP_WIDTH}px`;
    this.root.style.height = `${SHOP_HEIGHT}px`;

    this.titleEl = elem(doc, "div", "m2-titlebar shop-title");
    this.titleEl.id = "shop-title";
    // `SHOP_TITLE` (`locale_interface.txt:298`).
    this.titleEl.textContent = SHOP_TITLE;
    this.root.setAttribute("aria-labelledby", this.titleEl.id);
    this.wireDrag(this.titleEl);

    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close shop-close";
    close.title = "Close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.dismiss());

    this.grid = elem(doc, "div", "shop-grid");
    for (let i = 0; i < SHOP_SLOT_COUNT; i++) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "shop-slot m2-slot";
      cell.dataset.slot = String(i);
      const index = i;
      cell.addEventListener("click", () => this.onCellClick(index));
      // One listener for the life of the cell: paint only refreshes `shelfAt`.
      attachHoverTooltip(cell, this.tooltip, () => {
        const shelf = this.shelfAt[index];
        return shelf === undefined ? null : shelfTooltipData(shelf);
      });
      this.cells.push(cell);
      this.grid.append(cell);
    }

    this.buyBtn = doc.createElement("button");
    this.buyBtn.type = "button";
    this.buyBtn.className = "shop-buy m2-middle shop-mode-on";
    // `SHOP_BUY` (`locale_interface.txt:296`).
    this.buyBtn.textContent = "Buy";
    this.buyBtn.addEventListener("click", () => this.setSellMode(false));

    this.sellBtn = doc.createElement("button");
    this.sellBtn.type = "button";
    this.sellBtn.className = "shop-sell m2-middle";
    // `SHOP_SELL` (`locale_interface.txt:297`).
    this.sellBtn.textContent = "Sell";
    this.sellBtn.addEventListener("click", () => this.setSellMode(true));

    this.emptyEl = elem(doc, "div", "shop-empty");
    this.emptyEl.hidden = true;

    this.hintEl = elem(doc, "div", "shop-hint");
    // `SHOP_BUY_INFO` (`locale_game.txt:430`), the line the original posts to
    // chat on opening a shop's buy tab (`uishop.py:259`).
    this.hintEl.textContent = "Choose the Item you want to buy in the shop window.";

    this.root.append(this.titleEl, close, this.grid, this.buyBtn, this.sellBtn, this.emptyEl, this.hintEl);
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.dismiss();
    };
    this.root.addEventListener("keydown", onEscape);
    doc.addEventListener("keydown", (event) => {
      if (!this.root.isConnected) return;
      onEscape(event);
    });
    container.append(this.root);
  }

  update(stock: ShopStock): void {
    if (stock.npcId === 0) {
      this.hide();
      return;
    }
    this.stock = stock;
    this.root.hidden = false;
    this.paint();
  }

  /** What the player can afford, from the last `Bag`. Redraws only when the
   *  affordability of something actually changed -- see `paint`. */
  setGold(gold: number): void {
    this.gold = gold;
    if (this.showing) this.paint();
  }

  private setSellMode(selling: boolean): void {
    if (this.sellMode === selling) return;
    this.sellMode = selling;
    this.buyBtn.classList.toggle("shop-mode-on", !selling);
    this.sellBtn.classList.toggle("shop-mode-on", selling);
    // `SHOP_BUY_INFO` / `SHOP_SELL_INFO` (`locale_game.txt:430,438`).
    this.hintEl.textContent = selling
      ? "Choose the Item you want to sell in your inventory window."
      : "Choose the Item you want to buy in the shop window.";
    this.actions.onSellMode?.(selling);
  }

  private onCellClick(index: number): void {
    const shelf = this.shelfAt[index];
    if (shelf === undefined) return;
    this.actions.onBuy?.(shelf.slot);
  }

  private wireDrag(handle: HTMLElement): void {
    handle.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      const rect = this.root.getBoundingClientRect();
      this.drag = {
        ox: event.clientX,
        oy: event.clientY,
        sl: rect.left,
        st: rect.top,
      };
      handle.setPointerCapture(event.pointerId);
    });
    handle.addEventListener("pointermove", (event) => {
      if (this.drag === null) return;
      const left = this.drag.sl + (event.clientX - this.drag.ox);
      const top = this.drag.st + (event.clientY - this.drag.oy);
      this.root.style.left = `${Math.round(left)}px`;
      this.root.style.top = `${Math.round(top)}px`;
      this.root.style.right = "auto";
    });
    const endDrag = () => {
      this.drag = null;
    };
    handle.addEventListener("pointerup", endDrag);
    handle.addEventListener("pointercancel", endDrag);
  }

  private paint(): void {
    const print = [
      this.stock.npcId,
      this.stock.name,
      ...this.stock.items.map(
        (i) =>
          `${i.slot}:${i.vnum}:${i.name}:${i.count}:${i.price}:` +
          `${affordable(i.price, this.gold) ? 1 : 0}`,
      ),
    ].join("|");
    if (print === this.fingerprint) return;
    this.fingerprint = print;

    const doc = this.root.ownerDocument;
    this.titleEl.textContent = SHOP_TITLE;

    const bySlot = new Map<number, ShopSlot_>();
    for (const shelf of this.stock.items) {
      if (shelf.slot >= 0 && shelf.slot < SHOP_SLOT_COUNT) {
        bySlot.set(shelf.slot, shelf);
      }
    }

    const originOf: (number | undefined)[] = Array.from({ length: SHOP_SLOT_COUNT });
    const spanOf = new Map<number, number>();
    for (const [slot, shelf] of bySlot) {
      const row = Math.floor(slot / SHOP_COLUMNS);
      const span = Math.min(iconCellsTall(shelf.vnum), SHOP_ROWS - row);
      spanOf.set(slot, span);
      originOf[slot] = slot;
      for (let step = 1; step < span; step++) {
        const covered = slot + step * SHOP_COLUMNS;
        if (covered < SHOP_SLOT_COUNT && originOf[covered] === undefined) {
          originOf[covered] = slot;
        }
      }
    }

    for (let i = 0; i < SHOP_SLOT_COUNT; i++) {
      const cell = this.cells[i];
      const origin = originOf[i];
      if (origin === undefined) {
        this.shelfAt[i] = undefined;
        this.clearCell(cell);
        continue;
      }
      const shelf = bySlot.get(origin);
      if (shelf === undefined) {
        this.shelfAt[i] = undefined;
        this.clearCell(cell);
        continue;
      }
      this.shelfAt[i] = shelf;
      if (origin !== i) {
        this.clearCell(cell);
        cell.className = "shop-slot m2-slot shop-slot-covered";
        const label = shelfLabel(shelf.name, shelf.count);
        cell.title = `${label} — ${money(shelf.price)}`;
        cell.setAttribute("aria-label", label);
        continue;
      }

      const within = affordable(shelf.price, this.gold);
      const span = spanOf.get(i) ?? 1;
      // `.shop-row` stays on origin cells so existing icon tests that query it
      // keep finding shelves; the cell number is still `shelf.slot`.
      cell.className =
        `shop-slot m2-slot shop-row shop-slot-${shelf.kind}` +
        (within ? "" : " shop-row-unaffordable");
      cell.style.setProperty("--shop-item-span", String(span));
      cell.style.zIndex = span > 1 ? "1" : "";
      const label = shelfLabel(shelf.name, shelf.count);
      cell.title = `${label} — ${money(shelf.price)}`;
      cell.setAttribute("aria-label", label);

      cell.replaceChildren();
      const art = itemArtNode(shelf.vnum, shelf.kind, doc, "shop-glyph");
      cell.append(art);

      if (shelf.count > 1) {
        const count = elem(doc, "span", "shop-count", String(shelf.count));
        cell.append(count);
      }

      const price = elem(doc, "span", "shop-price", money(shelf.price));
      cell.append(price);
    }

    if (this.stock.items.length === 0) {
      this.emptyEl.hidden = false;
      // Honest about what this is: the shelves exist in the shop table and this
      // shard's item table has no definition for any of them, so there is
      // nothing it could hand over. Saying "sold out" would be a lie about the
      // game rather than a fact about the build.
      this.emptyEl.textContent = "Nothing this dealer stocks is available here.";
    } else {
      this.emptyEl.hidden = true;
      this.emptyEl.textContent = "";
    }
  }

  /** Close because the player asked. Tells the server, so its record of the
   *  open counter goes with the panel. */
  dismiss(): void {
    if (!this.showing) return;
    this.hide();
    this.actions.onClose?.();
  }

  /** Close without telling anyone -- for a server frame that already said so. */
  hide(): void {
    this.root.hidden = true;
    this.stock = { npcId: 0, name: "", items: [] };
    this.fingerprint = "";
    if (this.sellMode) this.setSellMode(false);
    for (let i = 0; i < SHOP_SLOT_COUNT; i++) {
      this.shelfAt[i] = undefined;
      this.clearCell(this.cells[i]);
    }
    this.emptyEl.hidden = true;
    this.emptyEl.textContent = "";
    this.tooltip.hide();
  }

  /** Which dealer is on screen, or null when nothing is. */
  get dealer(): number | null {
    return this.showing ? this.stock.npcId : null;
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }

  private clearCell(cell: HTMLButtonElement): void {
    cell.replaceChildren();
    cell.className = "shop-slot m2-slot";
    cell.title = "";
    cell.removeAttribute("aria-label");
    cell.style.removeProperty("--shop-item-span");
    cell.style.zIndex = "";
  }
}
